import { html, LitElement } from 'lit';
import { customElement } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_PATCH_MANAGEMENT_MAINTENANCE_WINDOW_SUMMARY } from '../../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import { styles } from '../view/styles';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { TemplateResult } from 'lit';
import {
  MaintenanceWindowPreviewPatchManagementWebComponentTemplateDataV2,
  SummaryData,
} from '../../utils/MaintenanceWindowSummaryConverterV2';
import { TextHighlightMixin } from '../../../../../../../../mixins';
import { connect } from 'pwa-helpers';
import { RootState, store } from '@nirvana/redux/store';
import { selectAccordionsSearchState } from '@nirvana/redux/searchable-ui-accordions';
import { getPatchManagementRebootSearchableKeyword } from '../../../../../utils/uiSettings/patchManagement/patch-management.values';
import { PatchManagementSearchableKeywords } from '../../../../../utils/uiSettings/patchManagement/patch-management.types';

@customElement(WEB_COMPONENT_POLICY_PANEL_PATCH_MANAGEMENT_MAINTENANCE_WINDOW_SUMMARY)
export class GzMaintenanceWindowSummaryWebComponent extends TextHighlightMixin(connect(store)(LitElement)) {
  /**
   * Show/Hide title.
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  isTitleHidden: boolean;

  /**
   * Summary data to show.
   */
  @property({ type: Object, attribute: true, reflect: true })
  summaryData: SummaryData;

  // searchable keywords for the component
  private searchableKeywords = getPatchManagementRebootSearchableKeyword();

  /**
   * Styles for the component.
   */
  static get styles() {
    return styles;
  }

  /**
   * State changed, update search string.
   * @param rootState
   */
  public stateChanged(rootState: RootState) {
    const accordionsSearchApp = selectAccordionsSearchState(rootState);

    this.searchString = accordionsSearchApp.searchString;
  }

  /**
   * Render specific content.
   */
  protected render() {
    const name =
      this.summaryData.name !== ''
        ? translateFn('Summary for: {maintenanceWindowName}', {
            maintenanceWindowName: this.summaryData.name,
          })
        : translateFn('Maintenance Window Summary');

    const classes = {
      hidden: this.isTitleHidden,
    };

    return html`
      <gz-title class="${classMap(classes)} form-subtitle" id="title" fontSize="11" fontWeight="600">${name}</gz-title>

      <div>${this.renderCardsForSummary(this.summaryData.previews)}</div>
    `;
  }

  /**
   * Render multiple cards based on recurrences.
   * @param previews
   */
  private renderCardsForSummary(
    previews: MaintenanceWindowPreviewPatchManagementWebComponentTemplateDataV2[]
  ): TemplateResult[] {
    return previews.map(preview => {
      return this.renderCard(preview.targetedOperations, preview.patchScope, preview.recurrence, preview.reboot);
    });
  }

  /**
   * Render a card with the given data.
   * @param targetedOperations
   * @param patchScope
   * @param recurrence
   * @param reboot
   */
  private renderCard(
    targetedOperations: string,
    patchScope: string,
    recurrence: string,
    reboot?: string
  ): TemplateResult {
    const classes = {
      hidden: !reboot,
    };

    return html`
      <gz-card class="window-summary" name="${translateFn('Schedule details')}">
        <gz-labeled-content
          id="operations-targeted"
          label="${translateFn('Target operations')}"
          hasColon
          labelFontSize="12"
          clampLine="true"
        >
          <gz-text fontsize="12">${targetedOperations}</gz-text>
        </gz-labeled-content>

        <gz-labeled-content
          id="patch-scope"
          label="${translateFn('Patch scope')}"
          hasColon
          labelFontSize="12"
          clampLine="true"
        >
          <gz-text fontsize="12">${patchScope}</gz-text>
        </gz-labeled-content>

        <gz-labeled-content
          id="recurrence"
          label="${translateFn('Recurrence')}"
          hasColon
          labelFontSize="12"
          clampLine="true"
        >
          <gz-text fontsize="12">${recurrence}</gz-text>
        </gz-labeled-content>

        <gz-labeled-content
          searchable-label="${this.searchableKeywords[PatchManagementSearchableKeywords.PatchReboot].label}"
          class="${classMap(classes)}"
          id="reboot"
          label="${this.searchableKeywords[PatchManagementSearchableKeywords.PatchReboot].translatedLabel}"
          hasColon
          labelFontSize="12"
          clampLine="true"
        >
          <gz-text fontsize="12">${reboot}</gz-text>
        </gz-labeled-content>
      </gz-card>
    `;
  }
}
